import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, Download, X, Filter, ChevronDown } from 'lucide-react';

// Filter categories
type FilterCategory = 'all' | 'mental-health' | 'substance-use' | 'emotional-crisis' | 'free-pdfs' | 'posters' | 'psychotherapy';

interface FilterOption {
    id: FilterCategory;
    label: string;
    count: number;
}

// Resource data structure
interface Resource {
    id: string;
    title: string;
    description: string;
    url?: string;
    downloadUrl?: string;
    category: FilterCategory[];
    bgColor: string;
    textColor: string;
    type: 'external' | 'poster';
    thumbnail?: string;
}

export const ResourcesPage: React.FC = () => {
    const [activeFilter, setActiveFilter] = useState<FilterCategory>('all');
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // All resources combined
    const resources: Resource[] = [
        // External Resources
        {
            id: 'colorado-crisis',
            title: 'Colorado Crisis Services',
            description: 'CCS is Colorado\'s first statewide resource for mental health, substance use or emotional crisis help, information and referrals.',
            url: 'https://coloradocrisisservices.org/',
            category: ['mental-health', 'substance-use', 'emotional-crisis'],
            bgColor: '#1b6a9c',
            textColor: '#ffffff',
            type: 'external',
            thumbnail: '/images/Crisis Center.jpg'
        },
        {
            id: 'emdr',
            title: 'EMDR',
            description: 'Eye Movement Desensitization and Reprocessing (EMDR) therapy is an extensively researched, effective psychotherapy method.',
            url: 'https://www.emdria.org/',
            category: ['psychotherapy', 'mental-health'],
            bgColor: '#2c7873',
            textColor: '#ffffff',
            type: 'external',
            thumbnail: '/images/Eye Therapy.jpg'
        },
        {
            id: 'trauma-coe',
            title: 'Trauma',
            description: 'CoE is committed to advancing the implementation of high-quality treatment for individuals with physical & mental health conditions.',
            url: 'https://www.samhsa.gov/trauma-violence',
            category: ['mental-health', 'psychotherapy'],
            bgColor: '#d68800',
            textColor: '#ffffff',
            type: 'external',
            thumbnail: '/images/Trauma Help.jpeg'
        },
        // Resource Posters
        {
            id: 'ptsd-symptoms',
            title: 'PTSD Symptoms',
            description: 'Understanding the signs and symptoms of Post-Traumatic Stress Disorder',
            category: ['mental-health', 'posters', 'free-pdfs'],
            bgColor: '#e8d5c4',
            textColor: '#3d3d3d',
            type: 'poster',
            thumbnail: '/images/PTSD Symptoms.png'
        },
        {
            id: 'eating-disorders',
            title: 'Eating Disorders Overview',
            description: 'A comprehensive guide to understanding eating disorders including Anorexia, Bulimia, and Binge Eating',
            category: ['mental-health', 'posters', 'free-pdfs'],
            bgColor: '#c4d4e8',
            textColor: '#3d3d3d',
            type: 'poster',
            thumbnail: '/images/Eating Disorders Overview.png'
        },
        {
            id: 'bipolar-disorder',
            title: 'Bipolar Disorder',
            description: 'Understanding Mania, Depression, and Treatment options for Bipolar Disorder',
            category: ['mental-health', 'posters', 'free-pdfs'],
            bgColor: '#d4c4e8',
            textColor: '#3d3d3d',
            type: 'poster',
            thumbnail: '/images/Bipolar Disorder.png'
        },
        {
            id: 'anxiety-management',
            title: 'Anxiety Management',
            description: 'Practical strategies and techniques for managing anxiety in daily life',
            category: ['mental-health', 'posters', 'free-pdfs', 'psychotherapy'],
            bgColor: '#c4e8d4',
            textColor: '#3d3d3d',
            type: 'poster',
            thumbnail: '/images/Anxiety Mangement.png'
        },
        {
            id: 'depression-guide',
            title: 'Understanding Depression',
            description: 'Signs, symptoms, and treatment approaches for depression',
            category: ['mental-health', 'posters', 'free-pdfs'],
            bgColor: '#e8e4c4',
            textColor: '#3d3d3d',
            type: 'poster',
            thumbnail: '/images/Depression Guide.jpg'
        },
        {
            id: 'grief-healing',
            title: 'Grief & Healing',
            description: 'Navigating the journey through grief and finding paths to healing',
            category: ['emotional-crisis', 'mental-health', 'posters', 'free-pdfs'],
            bgColor: '#e8c4c4',
            textColor: '#3d3d3d',
            type: 'poster',
            thumbnail: '/images/Grief Healing.jpg'
        }
    ];

    // Filter options with counts
    const filterOptions: FilterOption[] = [
        { id: 'all', label: 'All Resources', count: resources.length },
        { id: 'mental-health', label: 'Mental Health', count: resources.filter(r => r.category.includes('mental-health')).length },
        { id: 'substance-use', label: 'Substance Use', count: resources.filter(r => r.category.includes('substance-use')).length },
        { id: 'emotional-crisis', label: 'Emotional Crisis', count: resources.filter(r => r.category.includes('emotional-crisis')).length },
        { id: 'free-pdfs', label: 'Free PDFs', count: resources.filter(r => r.category.includes('free-pdfs')).length },
        { id: 'posters', label: 'Posters', count: resources.filter(r => r.category.includes('posters')).length },
        { id: 'psychotherapy', label: 'Psychotherapy', count: resources.filter(r => r.category.includes('psychotherapy')).length },
    ];

    // Filtered resources
    const filteredResources = activeFilter === 'all'
        ? resources
        : resources.filter(r => r.category.includes(activeFilter));

    const openLightbox = (resource: Resource) => {
        if (resource.type === 'poster') {
            setSelectedResource(resource);
            setLightboxOpen(true);
            document.body.style.overflow = 'hidden';
        }
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setSelectedResource(null);
        document.body.style.overflow = 'unset';
    };

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && lightboxOpen) closeLightbox();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [lightboxOpen]);

    return (
        <div className="resources-page bg-[#f5f0eb] min-h-screen">
            {/* Hero Section - Simple Library Header */}
            <section className="relative bg-[#293039] flex items-center justify-center" style={{ height: '55vh', minHeight: '320px' }}>
                <div className="text-center px-6">
                    <h1 className="text-5xl md:text-6xl lg:text-7xl pt-24 font-serif text-white mb-4 tracking-wide">
                        Resources
                    </h1>
                    <p className="text-white/60 text-lg md:text-xl max-w-xl mx-auto">
                        Helpful tools, worksheets, readings, & external links designed to complement your therapeutic work.
                        Take advantage of materials that support progress between sessions.
                    </p>
                </div>
            </section>

            {/* Main Content with Sidebar */}
            <div className="flex flex-col lg:flex-row" ref={contentRef}>
                {/* Mobile Filter Toggle */}
                <button
                    onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                    className="lg:hidden flex items-center justify-between w-full px-6 py-4 bg-white border-b border-neutral-200"
                >
                    <span className="flex items-center gap-2 font-medium text-mscg-dark-gray">
                        <Filter className="w-5 h-5" />
                        Filter Resources
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${mobileFilterOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Sidebar - Left Filter */}
                <aside className={`
                    lg:w-64 lg:min-w-[240px] bg-white lg:bg-transparent
                    lg:sticky lg:top-20 lg:h-fit lg:pt-8 lg:pl-6 lg:pr-4
                    border-r border-neutral-200/50
                    ${mobileFilterOpen ? 'block' : 'hidden lg:block'}
                `}>
                    <div className="p-6 lg:p-0">
                        <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-400 mb-4">
                            Categories
                        </h3>
                        <nav className="space-y-1">
                            {filterOptions.map((filter) => (
                                <button
                                    key={filter.id}
                                    onClick={() => {
                                        setActiveFilter(filter.id);
                                        setMobileFilterOpen(false);
                                    }}
                                    className={`
                                        w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all duration-200
                                        ${activeFilter === filter.id
                                            ? 'bg-[#293039] text-white'
                                            : 'text-neutral-700 hover:bg-neutral-100'
                                        }
                                    `}
                                >
                                    <span className="font-medium">{filter.label}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${activeFilter === filter.id
                                        ? 'bg-white/20 text-white'
                                        : 'bg-neutral-200 text-neutral-600'
                                        }`}>
                                        {filter.count}
                                    </span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Main Grid Content */}
                <main className="flex-1 p-6 lg:p-8 pb-24">
                    {/* Results Header */}
                    <div className="flex items-center justify-between mb-8">
                        <p className="text-neutral-600">
                            Showing <span className="font-semibold text-mscg-dark-gray">{filteredResources.length}</span> resources
                        </p>
                    </div>

                    {/* Resource Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredResources.map((resource) => (
                            <div
                                key={resource.id}
                                className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer"
                                style={{ backgroundColor: resource.bgColor }}
                                onClick={() => resource.type === 'poster' ? openLightbox(resource) : window.open(resource.url, '_blank')}
                            >
                                {/* Thumbnail Image */}
                                {resource.thumbnail && (
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <img
                                            src={resource.thumbnail}
                                            alt={resource.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </div>
                                )}

                                {/* Card Content */}
                                <div className={`p-5 ${resource.type === 'external' ? 'min-h-[200px] flex flex-col justify-between' : ''}`}>
                                    <div>
                                        <h3
                                            className="text-lg font-serif mb-2 transition-colors duration-300"
                                            style={{ color: resource.textColor }}
                                        >
                                            {resource.title}
                                        </h3>
                                        <p
                                            className="text-sm leading-relaxed mb-4"
                                            style={{ color: resource.textColor, opacity: 0.8 }}
                                        >
                                            {resource.description}
                                        </p>
                                    </div>

                                    {/* Action Button */}
                                    {resource.type === 'external' ? (
                                        <a
                                            href={resource.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 hover:gap-3"
                                            style={{ color: resource.textColor }}
                                        >
                                            <span>Read More</span>
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    ) : (
                                        <div
                                            className="flex items-center gap-2 text-sm font-medium"
                                            style={{ color: resource.textColor }}
                                        >
                                            <Download className="w-4 h-4" />
                                            <span>View & Download</span>
                                        </div>
                                    )}
                                </div>

                                {/* Category Tags */}
                                <div className="absolute top-3 right-3 flex flex-wrap gap-1 max-w-[120px] justify-end">
                                    {resource.type === 'external' ? (
                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-[#293039] text-white">
                                            External
                                        </span>
                                    ) : (
                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-[#d68800] text-white">
                                            Internal
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </main>
            </div>

            {/* Lightbox Modal */}
            {lightboxOpen && selectedResource && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
                    onClick={closeLightbox}
                >
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

                    <div
                        className="relative max-w-5xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        style={{ animation: 'lightboxIn 0.3s ease-out' }}
                    >
                        <button
                            onClick={closeLightbox}
                            className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all duration-300"
                            aria-label="Close"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            <div className="relative aspect-square lg:aspect-auto">
                                <img
                                    src={selectedResource.thumbnail}
                                    alt={selectedResource.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <div className="p-8 lg:p-12 flex flex-col justify-center" style={{ backgroundColor: selectedResource.bgColor }}>
                                <h2 className="text-3xl lg:text-4xl font-serif mb-6" style={{ color: selectedResource.textColor }}>
                                    {selectedResource.title}
                                </h2>

                                <p className="leading-relaxed mb-8 text-lg" style={{ color: selectedResource.textColor, opacity: 0.8 }}>
                                    {selectedResource.description}
                                </p>

                                <a
                                    href={selectedResource.thumbnail}
                                    download={`${selectedResource.title}.jpg`}
                                    className="inline-flex items-center gap-3 px-8 py-4 bg-white text-mscg-dark-gray font-medium rounded-full hover:bg-neutral-100 transition-all duration-300 w-fit group"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Download className="w-5 h-5 transition-transform duration-300 group-hover:-translate-y-0.5" />
                                    <span>Download Poster</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes lightboxIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
            `}</style>
        </div>
    );
};
